import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ReviewsViewContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { Text } from "@src/appV2/redesign/system/ui-components";
import {
  ReviewSource,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";

import { type CommentReactionSource } from "../types";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { CommentReactionButtons } from "./CommentReactionButtons";
import { GoogleReviewBadge } from "./ExternalReviewBadge/GoogleReviewBadge";
import { LockedReviewOverlay } from "./LockedReviewOverlay";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { WorkplaceReviewCommentRepliesCountIcon } from "./RepliesCountIcon";
import { VideoCommentPlayer } from "./VideoCommentPlayer";
import { WorkplaceReviewAnonymizeIcon } from "./WorkplaceReviewAnonymizeIcon";
import { WorkplaceReviewRemoveIcon } from "./WorkplaceReviewRemoveIcon";

interface CardStandardLayoutProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture: boolean;
  createdAt: Date;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymize: () => void;
  onRemove: () => void;
  isLocked?: boolean;
  onUnlockPress?: () => void;
  reviewUrl?: string;
  showRepliesCount?: boolean;
  reactionSource: CommentReactionSource;
  viewContext: ReviewsViewContext;
}

export function CardStandardLayout(props: CardStandardLayoutProps) {
  const {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl,
    isLoadingProfilePicture,
    createdAt,
    onLikeChanged,
    onAnonymize,
    onRemove,
    isLocked = false,
    onUnlockPress,
    reviewUrl,
    showRepliesCount = true,
    reactionSource,
    viewContext,
  } = props;

  const { workerInfo, text, aggregates, workerReaction, source } = comment.attributes;
  const { name: workerName } = workerInfo;
  const { totalReplies } = aggregates;
  const isGoogleReview = source === ReviewSource.GOOGLE;

  const videoUrl = comment.attributes.media?.[0]?.url;
  const isVideoComment = comment.attributes.type === "VIDEO" && isDefined(videoUrl);

  return (
    <>
      <Stack direction="row" spacing={3} alignItems="center">
        <WorkplaceReviewCommentAuthorInfo
          workerName={workerName}
          profilePictureUrl={profilePictureUrl}
          isLoadingProfilePicture={isLoadingProfilePicture}
          createdAt={createdAt}
          reviewUrl={reviewUrl}
        />
        {isGoogleReview && <GoogleReviewBadge />}
      </Stack>

      <LockedReviewOverlay isLocked={isLocked} onUnlockPress={onUnlockPress}>
        {isVideoComment && (
          <VideoCommentPlayer url={videoUrl} workplaceId={workplaceId} commentId={comment.id} />
        )}

        {text && (
          <Text variant="body2" sx={{ paddingBottom: 6 }}>
            {text}
          </Text>
        )}

        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <WorkplaceReviewCommentRepliesAndLikesWrapper>
            {viewerIsAuthor && <WorkplaceReviewAnonymizeIcon onAnonymize={onAnonymize} />}

            {showRepliesCount && <WorkplaceReviewCommentRepliesCountIcon count={totalReplies} />}

            <CommentReactionButtons
              workerReaction={workerReaction ?? undefined}
              totalLikes={aggregates.totalLikes}
              totalDislikes={aggregates.totalDislikes}
              workplaceId={workplaceId}
              commentId={comment.id}
              source={reactionSource}
              viewContext={viewContext}
              onReactionChanged={onLikeChanged}
            />

            {viewerIsAuthor && <WorkplaceReviewRemoveIcon onRemove={onRemove} />}
          </WorkplaceReviewCommentRepliesAndLikesWrapper>
        </Stack>
      </LockedReviewOverlay>
    </>
  );
}
